import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { CalendarIcon, Clock, Plus, ChevronDown, ChevronUp, Euro } from "lucide-react"
import { z } from "zod"
import { format } from "date-fns"
import { DayPicker } from "react-day-picker"
import "react-day-picker/style.css"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { Expense, Category, User } from "@/types/domain.types"
import type { FundingPool } from "@/api/schemas/funding-pool.schema"
import { useCreateCategory } from "@/hooks/mutations/useUpdateCategory"
import { useActiveGroup } from "@/stores/group.store"
import { logger } from "@/lib/logger"

const expenseFormSchema = z.object({
  description: z.string().min(1, "Description is required"),
  amount: z.number().min(0.01, "Amount must be greater than 0"),
  currency: z.string().min(1),
  date: z.string(),
  category_id: z.string().optional(),
  funding_pool_id: z.string().optional(),
  use_group_pool: z.boolean().optional(),
  paid_by_user_id: z.string(),
  splits: z.array(z.object({
    user_id: z.string(),
    amount: z.number(),
    weight: z.number().optional(),
  })),
})

type ExpenseFormData = z.infer<typeof expenseFormSchema>

interface ExpenseFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  expense?: Expense
  categories: Category[]
  groupMembers: User[]
  fundingPools?: FundingPool[]
  currentUserId: string
  onSubmit: (data: ExpenseFormData & { custom_splits?: CustomSplit[] }) => Promise<void>
  isSubmitting?: boolean
}

// Explicit user-chosen split weights. Only included in the submitted data when
// the user actively switches the split section to "Custom" — the default flow
// omits it so the backend derives splits (category defaults / equal split).
export interface CustomSplit {
  user_id: string
  weight: number
}

const toDateInputValue = (value?: string) => {
  if (!value) return ""

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0]
}

const toCalendarDate = (value: string) => {
  if (!value) return undefined

  const [year, month, day] = value.split("-").map(Number)
  if (!year || !month || !day) return undefined

  return new Date(year, month - 1, day)
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  open,
  onOpenChange,
  expense,
  categories,
  groupMembers,
  fundingPools = [],
  currentUserId,
  onSubmit,
  isSubmitting = false,
}) => {
  const [splitMethod, setSplitMethod] = React.useState<'equal' | 'custom'>('equal')
  // The explicit custom-split editor. Independent of the legacy splitMethod
  // machinery above: splitMode 'default' means the backend decides (category
  // defaults or equal split); 'custom' sends the weights below verbatim.
  const [splitMode, setSplitMode] = React.useState<'default' | 'custom'>('default')
  const [customWeights, setCustomWeights] = React.useState<Record<string, string>>({})
  const [selectedCategory, setSelectedCategory] = React.useState<Category | null>(null)
  const [dateMode, setDateMode] = React.useState<'now' | 'custom'>('now')
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>()
  const [showCategoryDialog, setShowCategoryDialog] = React.useState(false)
  const [newCategoryName, setNewCategoryName] = React.useState("")
  const [newCategoryEmoji, setNewCategoryEmoji] = React.useState("")
  const [amountInput, setAmountInput] = React.useState(expense?.amount ? String(expense.amount) : "")
  const { activeGroupId } = useActiveGroup()
  const createCategoryMutation = useCreateCategory()

  const form = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: {
      description: expense?.description || "",
      amount: expense?.amount || 0,
      currency: expense?.currency || expense?.currency_code || "EUR",
      date: "",
      category_id: "",
      funding_pool_id: "",
      use_group_pool: false,
      paid_by_user_id: currentUserId,
      splits: [],
    },
  })

  const watchedAmount = form.watch("amount")
  const watchedSplits = form.watch("splits")
  const useGroupPool = form.watch("use_group_pool")
  const formSessionRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      formSessionRef.current = null
      return
    }

    // The form stays mounted between opens. Rehydrate both React Hook Form and
    // the separate display state once per open/edit session so what the user
    // sees is exactly what will be submitted.
    const sessionId = expense ? `edit:${expense.id}` : "create"
    if (formSessionRef.current === sessionId) return
    formSessionRef.current = sessionId

    const amount = expense?.amount || 0
    const dateValue = toDateInputValue(expense?.date || expense?.created_at)
    const categoryId = expense?.category_id || expense?.category?.id || categories[0]?.id || ""
    const category = categories.find((candidate) => candidate.id === categoryId)
      || expense?.category
      || null
    const equalAmount = groupMembers.length > 0 ? amount / groupMembers.length : 0
    const splits = expense?.splits?.length
      ? expense.splits
      : groupMembers.map((member) => ({
          user_id: member.id,
          amount: equalAmount,
          weight: 1,
        }))

    form.reset({
      description: expense?.description || "",
      amount,
      currency: expense?.currency || expense?.currency_code || "EUR",
      date: dateValue,
      category_id: categoryId,
      funding_pool_id: "",
      use_group_pool: false,
      paid_by_user_id: expense?.paid_by_user_id || expense?.paid_by || currentUserId,
      splits,
    })

    setAmountInput(amount ? String(amount) : "")
    setSelectedCategory(category)
    setDateMode(dateValue ? 'custom' : 'now')
    setSelectedDate(toCalendarDate(dateValue))
    setSplitMethod('equal')
    setSplitMode('default')

    const existingWeights = expense?.weights
    const prefill: Record<string, string> = {}
    for (const member of groupMembers) {
      const existing = existingWeights?.find((weight) => weight.user_id === member.id)
      prefill[member.id] = existing ? String(existing.weight) : "1"
    }
    setCustomWeights(prefill)
  }, [categories, currentUserId, expense, form, groupMembers, open])

  const parsedCustomWeights: CustomSplit[] = groupMembers.map((member) => ({
    user_id: member.id,
    weight: Number((customWeights[member.id] ?? "1").replace(',', '.')),
  }))
  const customWeightsTotal = parsedCustomWeights.reduce(
    (sum, split) => sum + (Number.isFinite(split.weight) && split.weight > 0 ? split.weight : 0),
    0
  )
  const customSplitsValid = splitMode !== 'custom' || (
    parsedCustomWeights.every((split) => Number.isFinite(split.weight) && split.weight >= 0)
    && customWeightsTotal > 0
  )

  React.useEffect(() => {
    if (splitMethod === 'equal' && watchedAmount > 0) {
      const equalAmount = watchedAmount / groupMembers.length
      const newSplits = groupMembers.map(member => ({
        user_id: member.id,
        amount: equalAmount,
        weight: 1,
      }))
      form.setValue("splits", newSplits)
    }
  }, [watchedAmount, splitMethod, groupMembers, form])

  // Categories can finish loading after a new form has opened. Hydrate the
  // missing default without resetting any fields the user has already typed.
  React.useEffect(() => {
    if (!open || categories.length === 0) return

    const currentCategoryId = form.getValues("category_id")
    const currentCategory = categories.find((category) => category.id === currentCategoryId)

    if (currentCategory) {
      setSelectedCategory((selected) => selected?.id === currentCategory.id ? selected : currentCategory)
      return
    }

    if (!expense) {
      setSelectedCategory(categories[0])
      form.setValue("category_id", categories[0].id)
    }
  }, [categories, expense, form, open])

  const handleSubmit = async (data: ExpenseFormData) => {
    try {
      await onSubmit({
        ...data,
        ...(splitMode === 'custom' && customSplitsValid
          ? { custom_splits: parsedCustomWeights.filter((split) => split.weight > 0) }
          : {}),
      })
      onOpenChange(false)
      form.reset()
      setAmountInput("")
    } catch (error) {
      logger.error("Failed to submit expense:", error)
    }
  }

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category)
    form.setValue("category_id", category.id)

    if (category?.default_splits && category.default_splits.length > 0) {
      const totalWeight = category.default_splits.reduce((sum, split) => sum + split.weight, 0)
      const newSplits = category.default_splits.map(split => ({
        user_id: split.user_id,
        amount: (split.weight / totalWeight) * watchedAmount,
        weight: split.weight,
      }))
      form.setValue("splits", newSplits)
      setSplitMethod('custom')
    }
  }

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim() || !activeGroupId) return

    try {
      const newCategory = await createCategoryMutation.mutateAsync({
        groupId: activeGroupId,
        name: newCategoryName.trim(),
        emoji: newCategoryEmoji || "",
      })

      // Select the newly created category
      if (newCategory) {
        handleCategorySelect(newCategory)
      }

      // Reset dialog state
      setNewCategoryName("")
      setNewCategoryEmoji("")
      setShowCategoryDialog(false)
    } catch (error) {
      logger.error("Failed to create category:", error)
    }
  }

  const totalSplitAmount = watchedSplits.reduce((sum, split) => sum + split.amount, 0)
  const splitDifference = watchedAmount - totalSplitAmount

  const [payeeListOpen, setPayeeListOoen] = React.useState(false);

  const payeeIsUser = (userId: string) => {
    return userId == currentUserId;
  }

  const handleAmountChange = (raw: string, onChange: (value: number) => void) => {
    if (!/^\d*([.,]\d*)?$/.test(raw)) {
      return;
    }

    setAmountInput(raw);

    const normalized = raw.replaceAll(',', '.');
    if (raw === '' || normalized === '.') {
      onChange(0);
      return;
    }

    const value = Number(normalized);
    if (Number.isFinite(value)) {
      onChange(value);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {expense ? "Edit Expense" : "Add New Expense"}
          </DialogTitle>
          <DialogDescription>
            {expense
              ? "Update the expense details below."
              : "Enter the details for your new expense."
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="What was this expense for?"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="funding_pool_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Paid From (optional)</FormLabel>
                    <Select
                      value={useGroupPool ? "shared_pool" : (field.value || "personal")}
                      onValueChange={(value) => {
                        if (value === "personal") {
                          field.onChange("")
                          form.setValue("use_group_pool", false)
                          return
                        }
                        if (value === "shared_pool") {
                          field.onChange("")
                          form.setValue("use_group_pool", true)
                          return
                        }
                        field.onChange(value)
                        form.setValue("use_group_pool", false)
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select funding source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="personal">Personal funds</SelectItem>
                        <SelectItem value="shared_pool">Shared pool (auto create)</SelectItem>
                        {fundingPools.map((pool) => (
                          <SelectItem key={pool.id} value={pool.id}>
                            {pool.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Euro className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          className="pl-10"
                          value={amountInput}
                          onChange={(e) => handleAmountChange(e.target.value, field.onChange)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>When</FormLabel>
                <ToggleGroup
                  type="single"
                  value={dateMode}
                  onValueChange={(value: 'now' | 'custom') => {
                    if (value) {
                      setDateMode(value)
                      if (value === 'now') {
                        form.setValue('date', '')
                        setSelectedDate(undefined)
                      }
                    }
                  }}
                  className="justify-start"
                >
                  <ToggleGroupItem value="now" className="flex gap-2">
                    <Clock className="h-4 w-4" />
                    Now
                  </ToggleGroupItem>
                  <ToggleGroupItem value="custom" className="flex gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    Specific Date
                  </ToggleGroupItem>
                </ToggleGroup>

                {dateMode === 'custom' && (
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                              >
                                {selectedDate ? (
                                  format(selectedDate, "PPP")
                                ) : (
                                  <span className="text-muted-foreground">Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <DayPicker
                              mode="single"
                              selected={selectedDate}
                              onSelect={(date) => {
                                setSelectedDate(date)
                                field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                              }}
                              disabled={(date) =>
                                date > new Date() || date < new Date("1900-01-01")
                              }
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>

            <div className="space-y-3">
              <FormLabel>Category</FormLabel>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    type="button"
                    variant={selectedCategory?.id === category.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleCategorySelect(category)}
                  >
                    {category.emoji && (
                      <span className="mr-1">{category.emoji}</span>
                    )}
                    {category.name}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCategoryDialog(true)}
                  className="border-dashed"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Category
                </Button>
              </div>
            </div>

            <FormField
              control={form.control}
              name="paid_by_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={`flex items-center ${payeeIsUser(field.value) ? 'cursor-pointer pointer-events-auto' : 'pointer-events-none'}`} onClick={() => setPayeeListOoen(!payeeListOpen)}>
                    Paid by
                    {payeeIsUser(field.value) && <span className="ml-1 text-gray-700 font-bold">You</span>}
                    
                    {
                      payeeIsUser(field.value) &&
                      <span className="ml-2">
                        {
                          payeeListOpen ? <ChevronUp size="20" /> : <ChevronDown size="20" />
                        }
                      </span>
                    }
                  </FormLabel>
                  {
                    (payeeListOpen || !payeeIsUser(field.value)) &&
                    <div className="flex flex-wrap gap-2">
                      {groupMembers.map((member) => (
                        <Button
                          key={member.id}
                          type="button"
                          variant={field.value === member.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => field.onChange(member.id)}
                          className="flex items-center gap-2"
                        >
                          <Avatar className="h-5 w-5 text-black">
                            <AvatarImage src={member.avatar_url} />
                            <AvatarFallback className="text-xs">
                              {member.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          {member.name}
                        </Button>
                      ))}
                    </div>
                  }
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Split section: default = backend decides (category defaults or
                equal split); custom = these weights are sent and used verbatim. */}
            <div className="space-y-2">
              <FormLabel>Split</FormLabel>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={splitMode === 'default' ? "default" : "outline"}
                  onClick={() => setSplitMode('default')}
                >
                  {expense ? "Keep current" : "Default"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={splitMode === 'custom' ? "default" : "outline"}
                  onClick={() => setSplitMode('custom')}
                >
                  Custom
                </Button>
              </div>
              {splitMode === 'default' && !expense && (
                <p className="text-xs text-muted-foreground">
                  Uses the category's split if it has one, otherwise an equal split.
                </p>
              )}
              {splitMode === 'custom' && (
                <div className="space-y-2 rounded-lg border p-3">
                  {groupMembers.map((member) => {
                    const weight = parsedCustomWeights.find((split) => split.user_id === member.id)?.weight ?? 0
                    const share = customWeightsTotal > 0 && Number.isFinite(weight) && weight > 0
                      ? (watchedAmount * weight) / customWeightsTotal
                      : 0
                    return (
                      <div key={member.id} className="flex items-center gap-3">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="text-xs">{member.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span className="flex-1 text-sm truncate">{member.name}</span>
                        <Input
                          className="w-20 h-8 text-right"
                          inputMode="decimal"
                          value={customWeights[member.id] ?? "1"}
                          onChange={(e) =>
                            setCustomWeights((current) => ({ ...current, [member.id]: e.target.value }))
                          }
                        />
                        <span className="w-20 text-right text-xs text-muted-foreground tabular-nums">
                          {watchedAmount > 0 ? share.toFixed(2) : "—"}
                        </span>
                      </div>
                    )
                  })}
                  {!customSplitsValid && (
                    <p className="text-xs text-destructive">
                      Weights must be zero or higher, and at least one must be above zero.
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || Math.abs(splitDifference) > 0.01 || !customSplitsValid}
              >
                {isSubmitting ? "Saving..." : expense ? "Update Expense" : "Add Expense"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>

    <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Category</DialogTitle>
          <DialogDescription>
            Add a new category for organizing expenses
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              placeholder="Category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Emoji (optional)</label>
            <Input
              placeholder="e.g. 🍕"
              value={newCategoryEmoji}
              onChange={(e) => setNewCategoryEmoji(e.target.value)}
              maxLength={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setNewCategoryName("")
              setNewCategoryEmoji("")
              setShowCategoryDialog(false)
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
          >
            {createCategoryMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
